import { useRef, useState } from 'react';
import { HomeIcon, UploadIcon, Type, Star, MessageSquare, Quote, LayoutGrid, FormInput, CircleDot, Hexagon, type LucideIcon } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { tenantApi, type Asset, type CTA, type MultipleHomepageCreateRequest } from '../../lib/tenant-api';
import { toast } from 'react-toastify';
import { SortableField } from './SortableField';
import type { CustomFormFieldItem } from './types';

interface HomepageSectionsProps {
  homepageForm: MultipleHomepageCreateRequest & { imageSourceType?: string; imageAssetId?: string; imageUrl?: string };
  setHomepageForm: React.Dispatch<React.SetStateAction<any>>;
  assets: Asset[];
  setAssets: React.Dispatch<React.SetStateAction<Asset[]>>;
  ctas: CTA[];
  customFormFields: CustomFormFieldItem[];
  setCustomFormFields: React.Dispatch<React.SetStateAction<CustomFormFieldItem[]>>;
  customFormTitle: string;
  setCustomFormTitle: (v: string) => void;
  customFormSubmitText: string;
  setCustomFormSubmitText: (v: string) => void;
  customFormSubmitType: 'submit' | 'call_submit';
  setCustomFormSubmitType: (v: 'submit' | 'call_submit') => void;
  customFormPhoneNumber: string;
  setCustomFormPhoneNumber: (v: string) => void;
}

// Consistent icon + title header shown at the top of every homepage section card.
function SectionHeading({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="h-7 w-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-indigo-600" />
      </div>
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
    </div>
  );
}

export function useHomepageSections(props: HomepageSectionsProps) {
  const {
    homepageForm, setHomepageForm,
    assets, setAssets,
    ctas,
    customFormFields, setCustomFormFields,
    customFormTitle, setCustomFormTitle,
    customFormSubmitText, setCustomFormSubmitText,
    customFormSubmitType, setCustomFormSubmitType,
    customFormPhoneNumber, setCustomFormPhoneNumber,
  } = props;

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pendingLogoType, setPendingLogoType] = useState<'circle' | 'square' | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !pendingLogoType) return;

    try {
      setUploadingLogo(true);
      const logoTag = pendingLogoType === 'circle' ? 'logo_circle' : 'logo_square';
      const uploadedAsset = await tenantApi.uploadAsset(file, `Logo (${pendingLogoType})`, [logoTag]);
      setAssets(prev => [uploadedAsset, ...prev]);
      setHomepageForm((prev: any) => ({ ...prev, logo_asset_id: uploadedAsset.id }));
      toast.success(`Logo uploaded successfully!`);
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      setPendingLogoType(null);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
    }
  };

  const toggleAssetSelection = (assetId: string) => {
    const currentIds = homepageForm.selected_asset_ids || [];
    if (currentIds.includes(assetId)) {
      setHomepageForm((prev: any) => ({ ...prev, selected_asset_ids: currentIds.filter((id: string) => id !== assetId) }));
    } else {
      setHomepageForm((prev: any) => ({ ...prev, selected_asset_ids: [...currentIds, assetId] }));
    }
  };

  const toggleFieldEnabled = (fieldId: string) => {
    setCustomFormFields(fields => fields.map(f => f.id === fieldId ? { ...f, enabled: !f.enabled } : f));
  };

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setCustomFormFields((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = [...items];
        const [removed] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, removed);
        return newItems;
      });
    }
  };

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case 'logo':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8">
              <SectionHeading icon={Hexagon} title="Logo" />
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo Image</label>
              <select
                value={homepageForm.logo_asset_id || ''}
                onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, logo_asset_id: e.target.value || null }))}
                className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
              >
                <option value="">No Logo</option>
                {assets.filter(asset => asset.asset_type === 'image' && (asset.tags.includes('logo_circle') || asset.tags.includes('logo_square'))).map(asset => (
                  <option key={asset.id} value={asset.id}>
                    {asset.file_name} ({asset.tags.includes('logo_circle') ? 'Circle' : 'Square'})
                  </option>
                ))}
              </select>
              {homepageForm.logo_asset_id && (
                <div className="mt-3 logo-heading-spacing">
                  <img src={assets.find(a => a.id === homepageForm.logo_asset_id)?.custom_metadata.public_url} alt="Logo Preview" className="rounded-lg border w-32 h-32 object-cover" />
                </div>
              )}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Or Upload New Logo</label>
                <input ref={logoFileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                <div className="flex space-x-2">
                  <button type="button" onClick={() => { setPendingLogoType('circle'); logoFileInputRef.current?.click(); }} disabled={uploadingLogo} className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50 transition-colors">
                    <UploadIcon className="h-4 w-4 mr-1" />
                    {uploadingLogo && pendingLogoType === 'circle' ? 'Uploading...' : 'Upload Circle'}
                  </button>
                  <button type="button" onClick={() => { setPendingLogoType('square'); logoFileInputRef.current?.click(); }} disabled={uploadingLogo} className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50 transition-colors">
                    <UploadIcon className="h-4 w-4 mr-1" />
                    {uploadingLogo && pendingLogoType === 'square' ? 'Uploading...' : 'Upload Square'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'header':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8 space-y-4">
              <SectionHeading icon={Type} title="Header" />
              <div className="logo-heading-spacing">
                <label className="block text-sm font-medium text-gray-700">Header Title</label>
                <input type="text" value={homepageForm.header_title} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, header_title: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="Welcome to our service" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Header Subtitle</label>
                <input type="text" value={homepageForm.header_subtitle} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, header_subtitle: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="Your trusted partner for all your needs" />
              </div>
            </div>
          </div>
        );

      case 'rating':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8">
              <SectionHeading icon={Star} title="Rating" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rating (1-5 stars)</label>
                  <select value={homepageForm.rating_stars || ''} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, rating_stars: e.target.value ? parseInt(e.target.value) : null }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition">
                    <option value="">No rating</option>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} Star{n > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rating Text</label>
                  <input type="text" value={homepageForm.rating_text} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, rating_text: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="Rated 5 stars by 1000+ customers" />
                </div>
              </div>
            </div>
          </div>
        );

      case 'message':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8">
              <SectionHeading icon={MessageSquare} title="Message" />
              <label className="block text-sm font-medium text-gray-700">Message Text (Optional)</label>
              <textarea value={homepageForm.message_text} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, message_text: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" rows={3} placeholder="Leave empty for no message, or enter a custom message to display" />
              <p className="mt-1 text-xs text-gray-500">Leave empty to hide message section, or add custom content</p>
            </div>
          </div>
        );

      case 'testimonial':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8 space-y-4">
              <SectionHeading icon={Quote} title="Testimonial" />
              <div>
                <label className="block text-sm font-medium text-gray-700">Testimonial Quote (Optional)</label>
                <textarea value={homepageForm.testimonial_quote} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, testimonial_quote: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" rows={3} placeholder="Leave empty for no testimonial, or enter a customer testimonial" />
                <p className="mt-1 text-xs text-gray-500">Leave empty to hide testimonial section</p>
              </div>
              {homepageForm.testimonial_quote && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Testimonial Author</label>
                  <input type="text" value={homepageForm.testimonial_author} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, testimonial_author: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="John Doe, CEO at Company" />
                </div>
              )}
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8">
              <SectionHeading icon={LayoutGrid} title="Gallery" />
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Gallery Title</label>
                <input type="text" value={homepageForm.gallery_title} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, gallery_title: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="Before & After" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Assets</label>
                {assets.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-h-64 overflow-y-auto rounded-lg border border-gray-300 p-3 bg-white">
                    {assets.filter(asset => ['image', 'video'].includes(asset.asset_type)).map((asset) => (
                      <div key={asset.id} className="relative">
                        <div className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${(homepageForm.selected_asset_ids || []).includes(asset.id) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => toggleAssetSelection(asset.id)}>
                          <div className="aspect-square bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                            {asset.asset_type === 'image' ? (
                              <img src={asset.custom_metadata.public_url} alt={asset.file_name} className="w-full h-full object-cover " />
                            ) : (
                              <HomeIcon className="h-6 w-6 text-gray-400" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1 truncate">{asset.file_name}</p>
                          {(homepageForm.selected_asset_ids || []).includes(asset.id) && (
                            <div className="absolute -top-1 -right-1 bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">✓</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 rounded-lg border border-dashed border-gray-300 bg-white">
                    <HomeIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-500 mt-2">No image or video assets available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'form':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8">
              <SectionHeading icon={FormInput} title="Form" />
              <div>
                <label className="block text-sm font-medium text-gray-700">Select Form</label>
                <select value={homepageForm.selected_form_cta_id || ''} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, selected_form_cta_id: e.target.value || null }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition">
                  <option value="">No Form</option>
                  {ctas.filter(cta => cta.cta_type === 'form_email' || cta.cta_type === 'form_phone' || cta.cta_type === 'form_name' || cta.cta_type === 'form_full' || cta.cta_type === 'form_full_details').map((cta) => (
                    <option key={cta.id} value={cta.id}>{cta.name} - "{cta.text}"</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Select a predefined form or use custom form builder below</p>
              </div>

              <div className="my-6 relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true"><div className="w-full border-t border-gray-300"></div></div>
                <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">Or Build Custom Form</span></div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Form Title (Optional)</label>
                  <input type="text" value={customFormTitle} onChange={(e) => setCustomFormTitle(e.target.value)} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="Please Provide Your Details" />
                  <p className="mt-1 text-xs text-gray-500">Optional title displayed at the top of the form</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom Form Fields</label>
                  <p className="text-xs text-gray-500 mb-3">Check fields to enable, drag to reorder</p>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
                    <SortableContext items={customFormFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2 rounded-lg border border-gray-300 p-3 bg-white">
                        {customFormFields.map((field) => (
                          <SortableField key={field.id} field={field} onToggle={toggleFieldEnabled} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Submit Button Text</label>
                  <input type="text" value={customFormSubmitText} onChange={(e) => setCustomFormSubmitText(e.target.value)} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="Send Message" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Submit Button Type</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" value="submit" checked={customFormSubmitType === 'submit'} onChange={(e) => setCustomFormSubmitType(e.target.value as 'submit' | 'call_submit')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                      <span className="ml-2 text-sm text-gray-700">Regular Submit Button</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" value="call_submit" checked={customFormSubmitType === 'call_submit'} onChange={(e) => setCustomFormSubmitType(e.target.value as 'submit' | 'call_submit')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                      <span className="ml-2 text-sm text-gray-700">Call Submit Button</span>
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Choose whether to use a regular submit button or a call submit button with phone number</p>
                </div>
                {customFormSubmitType === 'call_submit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <input type="tel" value={customFormPhoneNumber} onChange={(e) => setCustomFormPhoneNumber(e.target.value)} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="982 444 0019" />
                    <p className="mt-1 text-xs text-gray-500">Phone number to be called when the submit button is clicked</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="pl-8">
              <SectionHeading icon={CircleDot} title="Call-to-Action" />
              <div>
                <label className="block text-sm font-medium text-gray-700">Select Button CTA</label>
                <select value={homepageForm.selected_cta_id || ''} onChange={(e) => setHomepageForm((prev: any) => ({ ...prev, selected_cta_id: e.target.value || null }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition">
                  <option value="">No Button CTA</option>
                  {ctas.filter(cta => cta.cta_type === 'generic' || cta.cta_type === 'link' || cta.cta_type === 'image_button' || cta.cta_type === 'appointment_booking').map((cta) => (
                    <option key={cta.id} value={cta.id}>{cta.name} - "{cta.text}"{cta.cta_type === 'image_button' ? ' (Image Button)' : ''}{cta.cta_type === 'appointment_booking' ? ' (Appointment Booking)' : ''}</option>
                  ))}
                </select>
                {(() => {
                  const selectedCta = ctas.find(cta => cta.id === homepageForm.selected_cta_id);
                  if (selectedCta && selectedCta.cta_type === 'image_button') {
                    let autoSourceType = '';
                    if (selectedCta.name.toLowerCase().includes('url')) autoSourceType = 'url';
                    else if (selectedCta.name.toLowerCase().includes('upload')) autoSourceType = 'upload';
                    if (homepageForm.imageSourceType !== autoSourceType) {
                      setHomepageForm((form: any) => ({ ...form, imageSourceType: autoSourceType }));
                    }
                    if (autoSourceType === 'upload') {
                      return (
                        <div className="mt-4 space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Choose Image Asset</label>
                          <select value={homepageForm.imageAssetId || ''} onChange={e => setHomepageForm((prev: any) => ({ ...prev, imageAssetId: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition">
                            <option value="">Select asset</option>
                            {assets.filter(asset => asset.asset_type === 'image').map(asset => (
                              <option key={asset.id} value={asset.id}>{asset.file_name}</option>
                            ))}
                          </select>
                          {homepageForm.imageAssetId && <img src={assets.find(asset => asset.id === homepageForm.imageAssetId)?.custom_metadata.public_url} alt="Preview" className="mt-2 rounded-lg border w-32 h-20 object-cover" />}
                        </div>
                      );
                    }
                    if (autoSourceType === 'url') {
                      return (
                        <div className="mt-4 space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Image URL</label>
                          <input type="url" value={homepageForm.imageUrl || ''} onChange={e => setHomepageForm((prev: any) => ({ ...prev, imageUrl: e.target.value }))} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="https://example.com/image.png" />
                          {homepageForm.imageUrl && <img src={homepageForm.imageUrl} alt="Preview" className="mt-2 rounded-lg border w-32 h-20 object-cover" onError={e => { e.currentTarget.style.display = 'none'; }} />}
                        </div>
                      );
                    }
                    return null;
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return { renderSection };
}
